import { PendingRule, PendingTrigger, ValidationMessage } from './types';
import { Trigger, Rule } from 'hellospa/conditional-logic/types';
import { defineMessages } from 'react-intl';
import { intl } from 'hellospa/components/hs-intl-provider';

const messages = defineMessages({
  noValue: {
    id: '',
    description: 'conditional logic, validators, validation error, informs user that no value was defined for rule trigger',
    defaultMessage: 'No value defined for trigger',
  },
  noNumber: {
    id: '',
    description: 'conditional logic, validators, validation error, informs user that value should not be a number',
    defaultMessage: 'No value must be a number',
  },
  noAction: {
    id: '',
    description: 'conditional logic, validators, validation error, informs user that action when rule fails/passes is required to create a rule',
    defaultMessage: 'No actions added to rule!',
  },
});

function validatePendingTrigger(trigger: PendingTrigger): Trigger | ValidationMessage {

  // I couldn't combine identical cases like `is` and `not`, because
  // TypeScript's type refinement isn't happy with `trigger.operator` being
  // multiple values.
  switch (trigger.operator) {
    case 'is':
      if (typeof trigger.value === 'boolean') {
        // If the value is a boolean, there is nothing to verify
      } else if (typeof trigger.value === 'string' && trigger.value.trim()) {
        // If it's a non-zero length string, then consider it valid
      } else {
        // The value is either missing or is an empty string.
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };
    case 'not':
      if (typeof trigger.value !== 'string' && typeof trigger.value !== 'boolean') {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };

    case 'gt': {
      const value = Number(trigger.value);
      if (isNaN(value)) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noNumber),
        };
      }
      return {
        id: trigger.id,
        operator: trigger.operator,
        value,
      };
    }
    case 'lt': {
      const value = Number(trigger.value);
      if (isNaN(value)) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noNumber),
        };
      }
      return {
        id: trigger.id,
        operator: trigger.operator,
        value,
      };
    }
    case 'has': {
      if (
        typeof trigger.value !== 'string'
        || trigger.value.trim().length === 0
      ) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };
    }
    case 'hasNot': {
      if (
        typeof trigger.value !== 'string'
        || trigger.value.trim().length === 0
      ) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };
    }
    case 'match': {
      if (
        typeof trigger.value !== 'string'
        || trigger.value.trim().length === 0
      ) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      try {
        /* eslint-disable no-new */
        new RegExp(String(trigger.value));
      } catch (e) {
        return {
          id: trigger.id,
          property: 'value',
          message: e.message,
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };
    }
    case 'any': {
      if (
        !Array.isArray(trigger.value)
        || trigger.value.length === 0
      ) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };
    }
    case 'none': {
      if (
        !Array.isArray(trigger.value)
        || trigger.value.length === 0
      ) {
        return {
          id: trigger.id,
          property: 'value',
          message: intl.formatMessage(messages.noValue),
        };
      }

      return { id: trigger.id, operator: trigger.operator, value: trigger.value };
    }
    default:
  }
  throw new Error(`Error validating trigger: ${trigger.id}`);
}

export default function validatePendingRule(pending: PendingRule): Rule | ValidationMessage {
  const triggers: Trigger[] = [];
  for (let i = 0; i < pending.triggers.length; i++) {
    const t = validatePendingTrigger(pending.triggers[i]);

    if ('operator' in t) {
      triggers.push(t);
    } else {
      return t;
    }
  }

  if (pending.actions.length === 0) {
    return {
      id: pending.id,
      property: 'actions',
      message: intl.formatMessage(messages.noAction),
    };
  }

  // PendingRule has a signer and Rule doesn't. Using a literal here ensures we
  // don't have any extra properties.
  return {
    id: pending.id,
    triggerOperator: pending.triggerOperator,
    triggers,
    actions: pending.actions,
  };
}
